// const VAT_RATE = 0.2;
import moment from "moment";
import { PAYMENT_INFO, TRANSACTION_TYPE } from "../constants/common";
import { getVatRateByCountryCode } from "../constants/vatRates";
import {
  OFFER_TYPE,
  PROMOTION_DISCOUNT_TYPE,
  LIMIT_TYPE,
  PURCHASE_STATE,
  ROLE
} from "../constants/common";
import { getCountryCode } from "./locationUtils";
import { reportError } from "shared/sentry";
import {
  calculateDaysRemaining,
  formatAmountWithTwoDecimalPlaces,
  formatAmountWithTwoDecimalPlacesNumber,
  formatDateTimeZone,
  getSubscriptionDateText
} from "./function";
// import apiCache from "./apiCache";

export const hasValidValue = (value) =>
  value !== null && value !== undefined && value !== "" && value !== "null";

export const getFormFieldValue = (fields, name) => {
  if (!Array.isArray(fields)) {
    return undefined;
  }
  return fields.find((field) => field.name === name)?.value;
};

export const isFreeTrialSubscriptionFunc = (creatorInfo) => {
  const paymentForm = creatorInfo?.subscription?.payment?.paymentForm;
  const daysRemaining = calculateDaysRemaining(creatorInfo?.subscription?.["expire_at"]);

  if (Array.isArray(paymentForm)) {
    const initialPrice = getFormFieldValue(paymentForm, 'initialPrice');
    if (!paymentForm || !initialPrice) {
      return false;
    }
    return parseFloat(initialPrice) === 0 &&
      creatorInfo?.subscription?.state === PURCHASE_STATE.EXPIRED &&
      daysRemaining >= 0
      ? true : false;
  }
  return false;
};

/**
 * Get the subscription end text based on expiry date
 * @param {string|number|Date} expireAt - Subscription expiry date
 * @returns {string} Text
 */
export const getSubscriptionText = (expireAt) => {
  if (!expireAt) {
    return "";
  }


  const daysRemaining = calculateDaysRemaining(expireAt);
  let text = "";
  if (daysRemaining > 0) {
    // text = `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`;
    text = `Ends ${formatDateTimeZone(expireAt, 'Mon DD')}`;
  } else if (daysRemaining === 0) {
    text = `Ends today`;
  }
  return text;
};

/**
 * Get the subscription end text based on expiry date
 * @param {string|number|Date} expireAt - Subscription expiry date
 * @returns {string} Button text
 */
export const getEndSubscriptionText = (expireAt) => {
  if (!expireAt) {
    return "";
  }


  const daysRemaining = calculateDaysRemaining(expireAt);
  if (daysRemaining > 0) {
    return `Ends ${formatDateTimeZone(expireAt, 'Mon DD')}`;
  } else if (daysRemaining === 0) {
    return `Ends today`;
  }
  return "";
};

/**
 * Get the VAT and service fee applied text for payment UI.
 * @param {number} vatDisplay - VAT amount to display
 * @returns {string} Text like "$X VAT fee and $Y service fee applied" (parts omitted when zero)
 */
export const getVatAndServiceFeeAppliedText = (vatDisplay) => {
  const hasVat = vatDisplay > 0;
  const extraCharge = PAYMENT_INFO.extraCharge ?? 0;
  const hasServiceFee = extraCharge > 0;
  const serviceFeeText = hasServiceFee
    ? `${formatAmountWithTwoDecimalPlaces(extraCharge)}`
    : "";

  if (hasVat && hasServiceFee) {
    return `Includes $${vatDisplay} VAT fee · $${serviceFeeText} service fee.`;
  }
  if (hasVat) {
    return `Includes $${vatDisplay} VAT fee.`;
  }
  if (hasServiceFee) {
    return `Includes $${serviceFeeText} service fee.`;
  }
  return '';
};

// return 25% Off or $10 Off or Free Trial
export const getDiscountOfferText = (promotionData) => {
  if (!promotionData) return '';

  let promotionText = '';

  if (promotionData && promotionData?._id &&
    // promotionData?.status === PROMOTION_STATUS.RUNNING &&
    promotionData?.active &&
    (promotionData?.offer_type === OFFER_TYPE.FREE_TRIAL ||
      (promotionData?.discount?.type === PROMOTION_DISCOUNT_TYPE.PERCENT &&
        promotionData?.discount?.value > 0) ||
      (PROMOTION_DISCOUNT_TYPE.DOLLAR && promotionData?.discount?.value > 0)
    )) {
    if (promotionData?.offer_type === OFFER_TYPE.FREE_TRIAL) {
      promotionText = 'Free Trial';
    } else if (promotionData?.discount?.type === PROMOTION_DISCOUNT_TYPE.PERCENT && promotionData?.discount?.value > 0) {
      promotionText = `${promotionData?.discount?.value}% Off `;
    } else if (promotionData?.discount?.type === PROMOTION_DISCOUNT_TYPE.DOLLAR && promotionData?.discount?.value > 0) {
      promotionText = `$${promotionData?.discount?.value} Off `;
    }
    return promotionText;
  }

  return;
};

/**
 * Get the offer type label for the Redeem button (e.g. "Discount", "Free Trial", null)
 * @param {Object} promotionData - The promotion data object
 * @returns {string|null} The label for the redeem button or null
 */
export const getRedeemOfferTypeLabel = (promotionData) => {
  if (!promotionData?.active) return null;
  if (promotionData?.offer_type === OFFER_TYPE.FREE_TRIAL) return 'Free Trial';
  if (promotionData?.offer_type === OFFER_TYPE.FIRST_MONTH) {
    if ((promotionData?.discount?.type === PROMOTION_DISCOUNT_TYPE.PERCENT && promotionData?.discount?.value > 0) ||
      (promotionData?.discount?.type === PROMOTION_DISCOUNT_TYPE.DOLLAR && promotionData?.discount?.value > 0)) {
      return 'Discount';
    }
    return null;
  }
  return null;
};

const FREE_TRIAL_MONTH_DURATION_LABELS = {
  60: "2 Months",
  120: "4 Months",
  180: "6 Months",
  240: "8 Months",
  300: "10 Months",
  360: "12 Months",
  365: "12 Months",
};

export const formatFreeTrialDurationText = (freeTrialDurationDays) => {
  const durationDays = Number(freeTrialDurationDays);

  if (!Number.isFinite(durationDays) || durationDays <= 0) {
    return "Free";
  }

  const roundedDays = Math.round(durationDays);
  const monthLabel = FREE_TRIAL_MONTH_DURATION_LABELS[roundedDays];

  if (monthLabel) {
    return `${monthLabel} Free`;
  }

  return `${roundedDays} ${roundedDays === 1 ? "Day" : "Days"} Free`;
};

const FREE_TRIAL_MONTH_DURATION_LABELS_SINGULAR = {
  60: "2 month",
  120: "4 month",
  180: "6 month",
  240: "8 month",
  300: "10 month",
  360: "12 month",
  365: "12 month",
};

export const formatFreeTrialDurationTextSingular = (freeTrialDurationDays) => {
  const durationDays = Number(freeTrialDurationDays);

  if (!Number.isFinite(durationDays) || durationDays <= 0) {
    return "Free";
  }

  const roundedDays = Math.round(durationDays);
  const monthLabel = FREE_TRIAL_MONTH_DURATION_LABELS_SINGULAR[roundedDays];

  if (monthLabel) {
    return `${monthLabel} free`;
  }

  return `${roundedDays} day free`;
};

/**
 * Get promotion remaining offer text based on promotion data
 * @param {Object} promotionData - The promotion data object
 * @returns {string|null} The text message for remaining offer or null
 */
export const getPromotionRemainingText = (promotionData) => {
  if (!promotionData) {
    return null;
  }

  // First Month offer type with redemptions available
  if (promotionData.limit_type === LIMIT_TYPE.FIXED) {
    if (promotionData.offer_remaining > 0) {
      const slotsLeft = promotionData.offer_remaining;
      // return { isValid: true, text: `${slotsLeft} ${slotsLeft === 1 ? 'slot' : 'slots'} left` };
      return { isValid: true, text: `${slotsLeft} left` };
    }
  } else if (promotionData.limit_type === LIMIT_TYPE.NO_LIMIT) {

    // First Month or Free Trial offer type with duration remaining
    if ((promotionData.offer_type === OFFER_TYPE.FIRST_MONTH &&
      promotionData.discount?.type === PROMOTION_DISCOUNT_TYPE.PERCENT &&
      promotionData.discount?.value > 0) ||
      (promotionData.offer_type === OFFER_TYPE.FREE_TRIAL)) {

      if (promotionData.ends_at > 0) {
        // Convert microseconds to milliseconds if timestamp is unusually large
        // Timestamps in microseconds are typically > 1e15 (year 2286+ in ms)
        // Normal millisecond timestamps for 2025 are around 1.7e12
        const endsAtMs = promotionData.ends_at > 1e15
          ? promotionData.ends_at / 1000
          : promotionData.ends_at;
        const endTime = moment(endsAtMs);
        const currentTime = moment();

        if (currentTime.isAfter(endTime)) {
          return { isValid: false, text: '' };
        }

        const remainingDays = Math.ceil(endTime.diff(currentTime, 'days', true));

        if (remainingDays > 0) {
          return {
            isValid: true,
            text: `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} left`
          };
        } else {
          return { isValid: false, text: '' };
        }
      }
      return { isValid: true, text: `` };
    }
  }

  return { isValid: false, text: '' };
};


/**
   * Calculate discount value based on promotion data
   * @param {Object} promotionData - The promotion data object
   * @param {number} subscriptionPrice - The original subscription price
   * @returns {number|null} The discount amount in dollars, or null if no discount applies
   */
export const getSubscriptionPrice = (promotionData, real_price, price) => {
  const MARKUP_PERCENTAGE = 20;

  const toAmount = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatAmount = (value) =>
    Number.isFinite(value) ? value.toFixed(2) : "0.00";

  const applyMarkup = (amount) =>
    amount + (amount * MARKUP_PERCENTAGE) / 100;

  const baseAmount = toAmount(real_price || price);
  const fallbackOriginalPrice = toAmount(price);

  const buildResult = (
    current,
    original,
    isFreeTrial = false,
    displayText = null,
    displayTextSingular = null
  ) => ({
    currentPrice: formatAmount(current),
    originalPrice: formatAmount(original),
    isFreeTrial: isFreeTrial,
    displayText: displayText,
    displayTextSingular: displayTextSingular,
  });

  if (baseAmount <= 0) {
    return buildResult(0, 0);
  }

  if (!promotionData || !promotionData.active) {
    const markedUpBase = applyMarkup(baseAmount);
    return buildResult(markedUpBase, 0);
  }

  const isFirstMonthOffer = promotionData.offer_type === OFFER_TYPE.FIRST_MONTH;
  const percentDiscount =
    promotionData.discount?.type === PROMOTION_DISCOUNT_TYPE.PERCENT
      ? toAmount(promotionData.discount.value)
      : 0;

  const hasFixedInventory =
    promotionData.limit_type === LIMIT_TYPE.FIXED &&
    promotionData.offer_limit > 0 &&
    promotionData.offer_remaining > 0;

  const hasUnlimitedInventory =
    promotionData.limit_type === LIMIT_TYPE.NO_LIMIT;

  const canApplyDiscount = hasFixedInventory || hasUnlimitedInventory;

  if (isFirstMonthOffer && percentDiscount > 0 && canApplyDiscount) {
    const discountedBaseAmount =
      baseAmount - (baseAmount * percentDiscount) / 100;
    const discountedTotal = applyMarkup(discountedBaseAmount);
    return buildResult(
      discountedTotal,
      fallbackOriginalPrice > 0 ? fallbackOriginalPrice : baseAmount
    );
  }

  const markedUpBase = applyMarkup(baseAmount);

  if (promotionData.offer_type === OFFER_TYPE.FREE_TRIAL) {
    let displayText = "Subscribe for Free";
    let displayTextSingular = "Subscribe for Free";
    if (promotionData.free_trial_duration_days > 0) {
      displayText = formatFreeTrialDurationText(promotionData.free_trial_duration_days);
      displayTextSingular = formatFreeTrialDurationTextSingular(promotionData.free_trial_duration_days);
    }
    return buildResult(markedUpBase, 0, true, displayText, displayTextSingular);
  }

  return buildResult(markedUpBase, 0);
};


export const buildPassThroughInfo = (fields) => {
  if (!Array.isArray(fields)) {
    return undefined;
  }

  const passthroughFields = fields.filter((field) => field.name.startsWith("c_"));
  return passthroughFields.length
    ? passthroughFields.map(({ name, value }) => ({ name, value }))
    : undefined;
};

export const extractPurchaseDetails = (responseData) => {
  let purchaseId = null;

  if (responseData?._id) {
    purchaseId = responseData._id;
  } else if (responseData?.paymentForm?.id) {
    purchaseId = responseData.paymentForm.id;
  }

  const nextPaymentForm =
    responseData?.paymentForm || responseData?.payment?.paymentForm;

  if (!purchaseId) {
    const c_payment_type = getFormFieldValue(nextPaymentForm, "c_payment_type");
    if (c_payment_type === TRANSACTION_TYPE.WALLET_CREDIT_PURCHASE) {
      purchaseId = getFormFieldValue(nextPaymentForm, "c_transaction_id");
    } else {
      purchaseId = getFormFieldValue(nextPaymentForm, "c_purchase_content_id");
    }
  }

  const paymentGateway =
    responseData?.paymentGateway || responseData?.payment?.paymentGateway;
  const paymentOneClick =
    responseData?.paymentOneClick ??
    responseData?.payment?.paymentOneClick ??
    null;

  return {
    purchaseId,
    nextPaymentForm,
    paymentGateway,
    paymentOneClick,
  };
};

export const getVatPercentage = (rate) => {
  const numericRate = Number(rate);
  if (!Number.isFinite(numericRate) || numericRate < 0) {
    return 0;
  }

  return numericRate > 1 ? numericRate / 100 : numericRate;
};

export const normalizeVatRate = (rate) => {
  const numericRate = Number(rate);
  if (!Number.isFinite(numericRate) || numericRate < 0) {
    return 0;
  }
  return numericRate;
};

export const resolveVatRate = async (userInfo) => {
  try {
    let countryCode = null;
    if (userInfo?.card_country_code) {
      countryCode = userInfo?.card_country_code;
    } else {
      countryCode = await getCountryCode();
      if (!countryCode || countryCode === null || countryCode === undefined) {
        countryCode = userInfo?.country ?? null;
      }
    }

    if (typeof countryCode === "string") {
      const normalizedCode = countryCode.trim().toUpperCase();
      if (normalizedCode) {
        const vatCountry = getVatRateByCountryCode.find(
          ({ code }) => code === normalizedCode
        );
        if (vatCountry && vatCountry.rate != null) {
          return normalizeVatRate(vatCountry.rate);
        }
      }
    }

    return normalizeVatRate(0);
  } catch (error) {
    reportError(error, "resolveVatRate");
    return normalizeVatRate(0);
  }
};

/**
 * Price + service fee (PAYMENT_INFO.extraCharge) + VAT, matching buildPurchasePayload totals.
 * @param {number|string} price
 * @param {number} vatPercentage - e.g. 20 for 20%
 * @returns {number}
 */
export const calculateAmountWithFeeAndVat = (price, vatPercentage = 0) => {
  const amount = Number.parseFloat(price) || 0;
  if (!Number.isFinite(amount) || amount <= 0) {
    return formatAmountWithTwoDecimalPlacesNumber(0);
  }

  const extraCharge = PAYMENT_INFO.extraCharge;
  const vatRate = getVatPercentage(vatPercentage ?? 0);
  const getAmount = amount + extraCharge;
  const vatAmount =
    Math.round(Math.round(getAmount * 100) * vatRate) / 100;

  return formatAmountWithTwoDecimalPlacesNumber(getAmount + vatAmount);
};

export const buildPurchasePayload = async (
  contentPrice,
  realPrice = 0,
  userInfo,
  { includeRecurringPrice = false } = {}
) => {
  const vatPercentage = await resolveVatRate(userInfo);
  const vatRate = getVatPercentage(vatPercentage ?? 0);
  const extraCharge = PAYMENT_INFO.extraCharge;
  const amount = Number.parseFloat(contentPrice) || 0;

  // || amount <= 0
  if (!Number.isFinite(amount)) {
    return null;
  }

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const returnUrl =
    origin + (userInfo?.username ? `/${userInfo.username}` : "");


  let getAmount = amount;
  if (getAmount > 0) {
    getAmount = getAmount + extraCharge;
  }
  // Compute VAT in cents and round: third decimal >= 5 rounds up (e.g. 9.045 -> 9.05)
  const vatAmount =
    Math.round(Math.round(getAmount * 100) * vatRate) / 100;
  const totalAmount = contentPrice > 0 ? getAmount + vatAmount : getAmount;

  // const formatCurrency = (value) =>
  //   Number.isFinite(value) ? Number(value.toFixed(2)) : 0;

  const formattedTotalAmount = formatAmountWithTwoDecimalPlacesNumber(totalAmount);
  const formattedVatAmount = formatAmountWithTwoDecimalPlacesNumber(vatAmount);
  const formattedRealAmount = formatAmountWithTwoDecimalPlacesNumber(amount);
  const formattedExtraCharge = contentPrice > 0 ? formatAmountWithTwoDecimalPlacesNumber(extraCharge) : 0;
  let formattedRealPrice = Number.parseFloat(realPrice) || 0;

  const payload = {
    amount: formattedTotalAmount,
    returnUrl,
    vatAmount: formattedVatAmount,
    vatPercentage: vatPercentage,
    baseAmount: formattedRealAmount,
    realPrice: formattedRealPrice,
    extraCharge: formattedExtraCharge
  };

  // Discount / free-trial: initial `amount` is promo price; recurring is full
  // realPrice with the same service fee + VAT used for paid checkouts.
  if (includeRecurringPrice && formattedRealPrice > 0) {
    payload.recurringPrice = calculateAmountWithFeeAndVat(
      formattedRealPrice,
      vatPercentage
    );
  }

  return payload;
};

export const buildTipPayload = async (
  contentPrice,
  realPrice = 0,
  userInfo
) => {
  // here we are updating because client changed thee logic many times so we need to update the real price here
  // Calculate 20% discount: contentPrice x 20% = discount, realPrice = contentPrice - discount
  if (contentPrice && Number.isFinite(Number.parseFloat(contentPrice))) {
    const discount = contentPrice * 0.20;
    realPrice = Number((Number(contentPrice) - Number(discount)).toFixed(2));
  }

  const vatPercentage = await resolveVatRate(userInfo);
  const vatRate = getVatPercentage(vatPercentage ?? 0);
  const extraCharge = PAYMENT_INFO.extraCharge;

  let amount = Number.parseFloat(contentPrice) || 0;
  if (!Number.isFinite(amount)) {
    return null;
  }

  if (amount > 0) {
    amount = amount + extraCharge;
  }

  const vatAmount = Number((amount * vatRate).toFixed(2));
  const totalAmount = Number((amount + vatAmount).toFixed(2));

  // const formatCurrency = (value) =>
  //   Number.isFinite(value) ? Number(value.toFixed(2)) : 0;

  const formattedTotalAmount = formatAmountWithTwoDecimalPlacesNumber(totalAmount);
  const formattedVatAmount = formatAmountWithTwoDecimalPlacesNumber(vatAmount);
  const formattedExtraCharge = contentPrice > 0 ? formatAmountWithTwoDecimalPlacesNumber(extraCharge) : 0;

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const returnUrl =
    origin + (userInfo?.username ? `/${userInfo.username}` : "");

  return {
    amount: formattedTotalAmount,
    returnUrl,
    vatAmount: formattedVatAmount,
    vatPercentage: vatPercentage,
    baseAmount: Number.parseFloat(contentPrice) || 0,
    realPrice: realPrice,
    extraCharge: formattedExtraCharge,
    platformFee: formatAmountWithTwoDecimalPlacesNumber(Number(contentPrice) - Number(realPrice)),
  };
};

/**
 * Detects credit card brand based on card number
 * @param {string} cardNumber - The credit card number to analyze
 * @returns {Object} - Object containing type, icon, and error properties
 */
export function detectCreditCard(cardNumber) {
  if (!cardNumber || typeof cardNumber !== 'string') {
    return { type: null, icon: null, error: 'Invalid input' };
  }

  // Remove spaces and hyphens
  const cleanedNumber = cardNumber.replace(/[\s-]/g, '');

  // Validate: Check if the input is numeric and 12-19 digits
  if (!/^\d{12,19}$/.test(cleanedNumber)) {
    return { type: null, icon: null, error: 'Invalid card number' };
  }

  // IIN ranges for card types
  const cardTypes = [
    {
      type: 'Visa',
      pattern: /^4/,
      icon: 'fa-brands fa-cc-visa',
    },
    {
      type: 'Mastercard',
      pattern: /^(5[1-5]|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[0-1][0-9]|2720)/,
      icon: 'fa-brands fa-cc-mastercard',
    },
    {
      type: 'JCB',
      pattern: /^(352[8-9]|35[3-8][0-9])/,
      icon: 'fa-brands fa-cc-jcb',
    },
    {
      type: 'Diners Club',
      pattern: /^(30[0-5]|36|38)/,
      icon: 'fa-brands fa-cc-diners-club',
    },
    {
      type: 'Discover',
      pattern: /^(6011|644|65)/,
      icon: 'fa-brands fa-cc-discover',
    },
    {
      type: 'Maestro',
      pattern: /^(5018|5020|5038|5893|6304|6759|676[1-3]|56[0-2][0-9]{2}|6333)/,
      icon: 'fa-brands fa-cc-mastercard', // fallback to mastercard icon
    },
  ];

  // Find matching card type
  const matchedCard = cardTypes.find(card => card.pattern.test(cleanedNumber));

  if (matchedCard) {
    return { type: matchedCard.type, icon: matchedCard.icon, error: null };
  }

  return { type: null, icon: null, error: 'Unsupported card type' };
}

export const getCardIcon = (brand) => {
  if (!brand) return 'fa-solid fa-credit-card';

  switch (brand.toLowerCase()) {
    case 'visa':
      return 'fa-brands fa-cc-visa';
    case 'mastercard':
      return 'fa-brands fa-cc-mastercard';
    case 'amex':
    case 'american-express':
      return 'fa-brands fa-cc-amex';
    case 'discover':
      return 'fa-brands fa-cc-discover';
    default:
      return 'fa-solid fa-credit-card';
  }
};

export const formatCardNumber = (lastFour) => {
  if (!lastFour) {
    return 'XXXX XXXX XXXX XXXX';
  }

  return `XXXX XXXX XXXX ${lastFour}`;
};

export const getAmountWithExtraCharge = (price, vatPercentage = 0) => {
  const vatRate = getVatPercentage(vatPercentage ?? 0);

  let getAmount = parseFloat(price) + parseFloat(PAYMENT_INFO.extraCharge);
  if (vatRate > 0) {
    const vatAmount =
      Math.round(Math.round(getAmount * 100) * vatRate) / 100;
    getAmount = getAmount + vatAmount;
  }
  return `${formatAmountWithTwoDecimalPlaces(getAmount)}`;
  // return (parseFloat(price) + parseFloat(PAYMENT_INFO.extraCharge)).toFixed(2);
};

export const getSubscriptionPriceWithExtraCharge = (price, promotionData = null, vatPercentage = 0) => {
  const vatRate = getVatPercentage(vatPercentage ?? 0);

  if (promotionData) {
    // Create a copy to avoid mutating Redux state
    const promotionDataCopy = { ...promotionData, active: true };
    let realPrice = ((price * 100) / 120).toFixed(2);
    let getSubscriptionPriceResult = getSubscriptionPrice(promotionDataCopy, realPrice, price);

    if (getSubscriptionPriceResult.isFreeTrial) {
      return 'Free Trial';
    } else if (getSubscriptionPriceResult.currentPrice) {
      let getAmount = parseFloat(getSubscriptionPriceResult.currentPrice) + parseFloat(PAYMENT_INFO.extraCharge);
      if (vatRate > 0) {
        const vatAmount =
          Math.round(Math.round(getAmount * 100) * vatRate) / 100;
        getAmount = getAmount + vatAmount;
      }
      // return `$${formatAmountWithTwoDecimalPlaces(getAmount)}/mo`;
      return `$${formatAmountWithTwoDecimalPlaces(getAmount)}`;
    }
  }

  let getAmount = parseFloat(price) + parseFloat(PAYMENT_INFO.extraCharge);
  if (vatRate > 0) {
    const vatAmount =
      Math.round(Math.round(getAmount * 100) * vatRate) / 100;
    getAmount = getAmount + vatAmount;
  }
  return `$${formatAmountWithTwoDecimalPlaces(getAmount)}/mo`;
};

/**
 * Subtitle for creator rows (search, recent): expiry text when subscribed, otherwise price or follow status.
 * Matches All Creators cards so an applied promo shows "Ends Oct 03" instead of the monthly price.
 */
export const getCreatorListSubtitle = (creator, vatPercentage = 0) => {
  if (!creator) return null;

  if (creator.rol === ROLE.CREATOR_CONTENT) {
    return creator.isFollowing ? 'Following' : 'Free to Follow';
  }

  if (creator.rol !== ROLE.CREATOR_SUB) {
    return null;
  }

  const expireAt = creator.expire_at || creator.expireAt;
  const hasAppliedPromotion =
    creator?.applied_promotion && Object.keys(creator.applied_promotion).length > 0;

  if (creator.isSubscribed && expireAt) {
    return getSubscriptionDateText(
      expireAt,
      creator.state,
      'all-creators',
      hasAppliedPromotion
    );
  }

  if (creator.price) {
    return getSubscriptionPriceWithExtraCharge(
      creator.price,
      creator?.active_promotion,
      vatPercentage
    );
  }

  return null;
};

export const getUserSubscriptionPrice = (price, paidAmount, applied_promotion, vatPercentage = 0) => {
  const vatRate = getVatPercentage(vatPercentage ?? 0);

  if (!applied_promotion || Object.keys(applied_promotion).length === 0) {
    let getAmount = parseFloat(price) + parseFloat(PAYMENT_INFO.extraCharge);
    if (vatRate > 0) {
      const vatAmount =
        Math.round(Math.round(getAmount * 100) * vatRate) / 100;
      getAmount = getAmount + vatAmount;
    }
    return `$${formatAmountWithTwoDecimalPlaces(getAmount)}/mo`;
  }
  if (applied_promotion && Object.keys(applied_promotion).length > 0) {
    if (applied_promotion.offer_type === OFFER_TYPE.FREE_TRIAL) {
      return applied_promotion?.duration + ' ' + (applied_promotion?.duration > 1 ? 'days' : 'day') + ' Free';
    } else {
      let getAmount = parseFloat(paidAmount) + parseFloat(PAYMENT_INFO.extraCharge);
      if (vatRate > 0) {
        const vatAmount =
          Math.round(Math.round(getAmount * 100) * vatRate) / 100;
        getAmount = getAmount + vatAmount;
      }
      return `$${formatAmountWithTwoDecimalPlaces(getAmount)}`;
    }
  }

  let getAmount = parseFloat(price) + parseFloat(PAYMENT_INFO.extraCharge);
  if (vatRate > 0) {
    const vatAmount =
      Math.round(Math.round(getAmount * 100) * vatRate) / 100;
    getAmount = getAmount + vatAmount;
  }
  return `$${formatAmountWithTwoDecimalPlaces(getAmount)}/mo`;
};

/**
 * Calculates price with 20% markup (e.g. 5 -> 6).
 * @param {number} price - Base price
 * @returns {number} Price with 20% added, rounded to 2 decimals
 */
export const calculatePriceWithMarkup = (price) => {
  const numeric = Number.isFinite(Number(price)) ? Number(price) : 0;
  return Number((numeric * (1 + PAYMENT_INFO.platformFeePercentage / 100)).toFixed(2));
}